import { useCallback, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

interface Props {
  readonly originalName: string;
  readonly error: string | null;
  readonly isLoading: boolean;
  readonly isOpen: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (newOriginalName: string) => void;
}

export const RenameFileDialog = ({
  originalName,
  error,
  isLoading,
  isOpen,
  onCancel,
  onConfirm,
}: Readonly<Props>) => {
  const [value, setValue] = useState(originalName);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setValue(originalName);
    }
  }

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }, []);

  const handleConfirm = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) {
      onConfirm(trimmed);
    }
  }, [value, onConfirm]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const trimmed = value.trim();
        if (trimmed) {
          onConfirm(trimmed);
        }
      }
    },
    [value, onConfirm],
  );

  const trimmedValue = value.trim();

  return (
    <Dialog
      aria-labelledby="rename-file-dialog-title"
      onClose={onCancel}
      open={isOpen}
    >
      <DialogTitle id="rename-file-dialog-title">
        Renommer le fichier
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Nom du fichier"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          sx={{ mt: 1 }}
          value={value}
        />
        {!!error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size="large">
          Annuler
        </Button>
        <Button
          color="primary"
          disabled={!trimmedValue || trimmedValue === originalName}
          loading={isLoading}
          onClick={handleConfirm}
          size="large"
          variant="contained"
        >
          Renommer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
